import { EChartsOption } from 'echarts';

export class ChartOptionsBuilder {
    /**
     * Crea la configuración de un gráfico de barras verticales para ECharts.
     * - Define el estilo visual del gráfico según el tema (oscuro o claro).
     * - Calcula dinámicamente el valor máximo del eje Y para mejorar la escala.
     * - Asocia los datos con categorías y leyendas para representar una única serie.
     */
    static createBarChartOption1(data: number[], categories: string[], legend: string[], isDarkTheme: boolean): EChartsOption {

        const textColor = isDarkTheme ? '#ffffffb8' : '#333'
        const gridColor = isDarkTheme ? '#888' : '#ccc';
        const maxDataValue = Math.max(...data);
        const adjustedValue = maxDataValue * 1.1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(adjustedValue)));
        let yAxisMax;

        if (adjustedValue < magnitude * 1.2) {
            yAxisMax = Math.ceil(adjustedValue / (magnitude / 10)) * (magnitude / 10);
        } else {
            yAxisMax = Math.ceil(adjustedValue / magnitude) * magnitude;
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            },
            grid: {
                top: '15%',
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
                backgroundColor: gridColor,
            },
            legend: {
                data: legend,
                top: '5%',
                left: 'center',
                textStyle: {
                    color: textColor,
                    fontSize: 10
                }
            },
            xAxis: [
                {
                    type: 'category',
                    data: categories,
                    axisTick: {
                        alignWithLabel: true
                    },
                    splitLine: {
                        show: true,
                        interval: 0,
                        lineStyle: {
                            color: gridColor
                        }
                    },
                    axisLabel: {
                        fontSize: 10,
                        color: textColor
                    }
                }
            ],
            yAxis: [
                {
                    type: 'value',
                    max: yAxisMax,
                    splitNumber: 10,
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: gridColor
                        }
                    },
                    axisLabel: {
                        fontSize: 10,
                        color: textColor
                    }
                }
            ],
            series: [
                {
                    name: legend[0],
                    type: 'bar',
                    barWidth: '60%',
                    data: data,
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 12,
                        formatter: '{c}'
                    },
                    itemStyle: {
                        color: '#6dbde8',
                    }
                }
            ]
        };
    }

    /**
     * Crea la configuración de un gráfico de barras para ECharts.
     * - Ajusta los colores y el estilo de la cuadrícula según el tema (oscuro o claro).
     * - Calcula automáticamente el valor máximo del eje Y para una mejor escala visual.
     * - Asocia los datos con sus respectivas categorías y leyenda para la visualización.
     */
    static createBarChartOption2(data: number[], categories: string[], legend: string[], isDarkTheme: boolean): EChartsOption {

        const textColor = isDarkTheme ? '#ffffffb8' : '#333'
        const gridColor = isDarkTheme ? '#888' : '#ccc';
        const maxDataValue = Math.max(...data);
        const adjustedValue = maxDataValue * 1.1;
        const magnitude = Math.pow(10, Math.floor(Math.log10(adjustedValue)));
        let yAxisMax;

        if (adjustedValue < magnitude * 1.2) {
            yAxisMax = Math.ceil(adjustedValue / (magnitude / 10)) * (magnitude / 10);
        } else {
            yAxisMax = Math.ceil(adjustedValue / magnitude) * magnitude;
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow'
                }
            },
            grid: {
                top: '15%',
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true,
                backgroundColor: gridColor,
            },
            legend: {
                data: legend,
                top: '5%',
                left: 'center',
                textStyle: {
                    color: textColor,
                    fontSize: 10
                }
            },
            xAxis: [
                {
                    type: 'category',
                    data: categories,
                    axisTick: {
                        alignWithLabel: true
                    },
                    splitLine: {
                        show: true,
                        interval: 0,
                        lineStyle: {
                            color: gridColor
                        }
                    },
                    axisLabel: {
                        rotate: 45,
                        interval: 0,
                        fontSize: 10,
                        color: textColor,
                    }
                }
            ],
            yAxis: [
                {
                    type: 'value',
                    max: yAxisMax,
                    splitNumber: 10,
                    splitLine: {
                        show: true,
                        lineStyle: {
                            color: gridColor
                        }
                    },
                    axisLabel: {
                        fontSize: 10,
                        color: textColor,
                    }
                }
            ],
            series: [
                {
                    name: legend[0],
                    type: 'bar',
                    barWidth: '60%',
                    data: data,
                    label: {
                        show: true,
                        position: 'top',
                        color: textColor,
                        fontSize: 10,
                        formatter: '{c}'
                    },
                    itemStyle: {
                        color: '#6dbde8',
                    }
                }
            ]
        };
    }

    /**
     * Crea la configuración de un gráfico de pastel tipo "donut" para ECharts.
     * - Ajusta colores de texto y bordes según si el tema es oscuro.
     * - Mapea los valores (`data`) y sus categorías (`categories`) para mostrar en la gráfica.
     */
    static createCakeChartOption (data: number[], categories: string[], isDarkTheme: boolean): EChartsOption {

        const textColor = isDarkTheme ? '#ffffffb8' : '#333'
        const borderColor = isDarkTheme ? '#1f1f31' : '#fff'

        return {
            tooltip: {
                trigger: 'item'
            },
            legend: {
                top: '5%',
                left: 'center',
                textStyle: {
                    color: textColor,
                }
            },
            series: [
                {
                    name: 'Access From',
                    type: 'pie',
                    radius: ['45%', '90%'],
                    top: '15%',
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 10,
                        borderColor: borderColor,
                        borderWidth: 2
                    },
                    label: {
                        show: false,
                        position: 'center'
                    },
                    emphasis: {
                        label: {
                            show: true,
                            fontSize: 40,
                            fontWeight: 'bold',
                            color: textColor,
                        }
                    },
                    labelLine: {
                        show: false
                    },
                    data: data.map((value, index) => ({
                        value: value,
                        name: categories[index]
                    }))
                }
            ]
        };
    }
}